// Programas anuales (lote C, M48): lectura y escritura en la base y en Storage (bucket privado annual-program-files,
// carpeta <empresa>/<programa>/: firmas/, escaneos/, evidencias/ y sut/). Archivos con URL firmada de 120 s.
// La base pone la fecha y hora de las firmas y de la aprobación, y no deja editar una versión aprobada (M48).

import { supabase } from '../../supabaseClient';
import { nombreSeguro, revisarEvidencia, type TipoEvidencia } from '../pe/pe-evidencias';
import {
  contenidoVersion, copiaParaVersionNueva, hashFirmaPrograma, hashPrograma, ordenarActividades, plantillaActividades,
  type FirmantePrograma, type NombresProgramacion,
} from './prog-logica';
import {
  ARCHIVO_MAX_BYTES, ARCHIVO_MAX_MB, BUCKET_PROGRAMAS, SEGUNDOS_URL_PROGRAMAS,
  type ActividadEditable, type CapacitacionMin, type ExamenMin, type FilaActividad, type FilaEjecucion, type FilaFirmaPrograma,
  type FilaPrograma, type FilaSut, type FilaVersion, type InspeccionMin, type MetodoFirma, type ProgramacionMin, type TipoPrograma,
  type TrabajadorMin,
} from './prog-tipos';

export const ACEPTA_ARCHIVO = '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf';

/** Mensaje claro para el cliente (sin detalles internos). */
export function mensajeError(e: { message?: string; code?: string } | null | undefined, accion: string): string {
  const m = e?.message ?? '';
  if (e?.code === '42501' || /row-level security|permission denied/i.test(m)) return `No tienes permiso para ${accion}.`;
  if (e?.code === '23505' || /duplicate key|unique/i.test(m)) return `No se pudo ${accion}: ya existe un registro igual.`;
  // Los mensajes de los triggers de la base ya están escritos para el cliente.
  if (e?.code === '23514' && m && !/violates check constraint/i.test(m)) return m;
  return `No se pudo ${accion}. Intenta de nuevo.`;
}
const falla = (e: { message?: string; code?: string } | null | undefined, accion: string): never => { throw new Error(mensajeError(e, accion)); };

/** ¿La base ya tiene los programas anuales? Sin las tablas, las pantallas no muestran el programa. */
export async function programasDisponibles(companyId: string): Promise<boolean> {
  if (!companyId) return false;
  const { error } = await supabase.from('annual_programs').select('id', { head: true, count: 'exact' }).eq('company_id', companyId).limit(1);
  return !error;
}

export interface DatosPrograma {
  programa: FilaPrograma | null;
  versiones: FilaVersion[];
  actividades: FilaActividad[];       // de todas las versiones
  firmas: FilaFirmaPrograma[];
  ejecuciones: FilaEjecucion[];
  sut: FilaSut | null;
}

export async function cargarPrograma(companyId: string, tipo: TipoPrograma, anio: number): Promise<DatosPrograma> {
  const { data: p, error } = await supabase.from('annual_programs').select('*').eq('company_id', companyId).eq('kind', tipo)
    .eq('program_year', anio).maybeSingle();
  if (error) falla(error, 'leer el programa');
  const programa = (p ?? null) as FilaPrograma | null;
  if (!programa) return { programa: null, versiones: [], actividades: [], firmas: [], ejecuciones: [], sut: null };
  const [{ data: v, error: e1 }, { data: a, error: e2 }, { data: e, error: e3 }, { data: s, error: e4 }] = await Promise.all([
    supabase.from('annual_program_versions').select('*').eq('program_id', programa.id).order('version'),
    supabase.from('annual_program_activities').select('*').eq('program_id', programa.id),
    supabase.from('annual_program_executions').select('*').eq('program_id', programa.id),
    supabase.from('annual_program_sut').select('*').eq('program_id', programa.id).maybeSingle(),
  ]);
  if (e1 || e2 || e3 || e4) falla(e1 ?? e2 ?? e3 ?? e4, 'leer el programa');
  const versiones = ((v ?? []) as FilaVersion[]).map(x => ({ ...x, settings: x.settings ?? {} }));
  const ids = versiones.map(x => x.id);
  let firmas: FilaFirmaPrograma[] = [];
  if (ids.length) {
    const { data: f, error: e5 } = await supabase.from('annual_program_signatures').select('*').in('version_id', ids).order('signed_at');
    if (e5) falla(e5, 'leer las firmas');
    firmas = (f ?? []) as FilaFirmaPrograma[];
  }
  return { programa, versiones, actividades: (a ?? []) as FilaActividad[], firmas, ejecuciones: (e ?? []) as FilaEjecucion[], sut: (s ?? null) as FilaSut | null };
}

export interface Origenes {
  trabajadores: TrabajadorMin[]; puestos: { id: string; name: string }[]; capacitaciones: CapacitacionMin[]; examenes: ExamenMin[];
  /** PO 14: programaciones e inspecciones de Inspecciones v2 y los nombres para armar el cronograma. */
  programaciones?: ProgramacionMin[]; inspecciones?: InspeccionMin[]; nombres?: NombresProgramacion;
  /** companies.created_at (plazo de aprobación de la empresa nueva). */
  empresaRegistrada?: string | null;
}

export async function cargarOrigenes(companyId: string, tipo?: TipoPrograma): Promise<Origenes> {
  const [{ data: w }, { data: p }, { data: c }, { data: x }, { data: emp }] = await Promise.all([
    supabase.from('workers').select('id, full_name, position, status, start_date, end_date, last_medical_exam').eq('company_id', companyId).order('full_name'),
    supabase.from('job_positions').select('id, name').eq('company_id', companyId).order('name'),
    supabase.from('sso_trainings').select('id, title, scheduled_date, status').eq('company_id', companyId).order('scheduled_date'),
    supabase.from('worker_medical_exams').select('id, worker_id, exam_date, exam_type').eq('company_id', companyId),
    supabase.from('companies').select('created_at').eq('id', companyId).maybeSingle(),
  ]);
  const base: Origenes = { trabajadores: (w ?? []) as TrabajadorMin[], puestos: (p ?? []) as { id: string; name: string }[],
    capacitaciones: (c ?? []) as CapacitacionMin[], examenes: (x ?? []) as ExamenMin[],
    empresaRegistrada: (emp as { created_at?: string } | null)?.created_at ?? null };
  if (tipo !== 'inspecciones') return base;
  const [{ data: s }, { data: t }, { data: sd }, { data: i }] = await Promise.all([
    supabase.from('insp_schedules').select('id, template_id, site_id, area, frequency, start_date, end_date, active, responsible_role, responsible_worker_id')
      .eq('company_id', companyId),
    supabase.from('insp_templates').select('id, name'),
    supabase.from('company_sites').select('id, name, active').eq('company_id', companyId),
    supabase.from('insp_inspections').select('id, schedule_id, template_name, status, scheduled_date, performed_date, signed_at').eq('company_id', companyId),
  ]);
  const sedes = (sd ?? []) as { id: string; name: string; active: boolean }[];
  return {
    ...base, programaciones: (s ?? []) as ProgramacionMin[], inspecciones: (i ?? []) as InspeccionMin[],
    nombres: {
      plantillas: Object.fromEntries(((t ?? []) as { id: string; name: string }[]).map(x => [x.id, x.name])),
      sedes: Object.fromEntries(sedes.map(x => [x.id, x.name])),
      trabajadores: Object.fromEntries(base.trabajadores.map(x => [x.id, x.full_name])),
      varias: sedes.filter(x => x.active).length > 1,
    },
  };
}

/** Riesgo psicosocial (ajuste del lote C): ¿hay constancia del SUT en el programa del año? (false si no hay M48). */
export async function constanciaSutDelAnio(companyId: string, tipo: 'psicosocial' | 'sustancias', anio: number): Promise<boolean> {
  if (!companyId) return false;
  const { data: p, error } = await supabase.from('annual_programs').select('id').eq('company_id', companyId).eq('kind', tipo)
    .eq('program_year', anio).maybeSingle();
  if (error || !p) return false;
  const { data: s, error: e2 } = await supabase.from('annual_program_sut').select('constancia_path').eq('program_id', (p as { id: string }).id).maybeSingle();
  return !e2 && !!(s as { constancia_path: string } | null)?.constancia_path;
}

// ── Programa y versiones ──
export async function crearPrograma(companyId: string, tipo: TipoPrograma, anio: number): Promise<void> {
  const { data, error } = await supabase.from('annual_programs').insert({ company_id: companyId, kind: tipo, program_year: anio }).select('id');
  if (error) falla(error, 'crear el programa');
  const programId = (data as { id: string }[])[0].id;
  const { data: v, error: e2 } = await supabase.from('annual_program_versions').insert({ company_id: companyId, program_id: programId, version: 1 }).select('id');
  if (e2) falla(e2, 'crear la versión del programa');
  const plantilla = plantillaActividades(tipo, anio);
  if (plantilla.length) await agregarActividades(companyId, programId, (v as { id: string }[])[0].id, plantilla);
}

export type CamposCabecera = Pick<FilaVersion, 'objectives' | 'scope_diagnosis' | 'methodology' | 'responsibles' | 'resources' | 'settings'>;
export async function guardarCabecera(v: FilaVersion, campos: Partial<CamposCabecera>): Promise<void> {
  const { data, error } = await supabase.from('annual_program_versions').update(campos).eq('id', v.id).select('id');
  if (error) falla(error, 'guardar el programa');
  if (!data?.length) throw new Error('No tienes permiso para cambiar el programa.');
}

export async function agregarActividades(companyId: string, programId: string, versionId: string, lista: ActividadEditable[], desde = 0): Promise<void> {
  if (!lista.length) return;
  const { error } = await supabase.from('annual_program_activities').insert(lista.map((a, i) => ({
    ...a, company_id: companyId, program_id: programId, version_id: versionId, position: desde + i,
  })));
  if (error) falla(error, 'agregar las actividades');
}

export async function editarActividad(id: string, cambios: Partial<ActividadEditable>): Promise<void> {
  const { data, error } = await supabase.from('annual_program_activities').update(cambios).eq('id', id).select('id');
  if (error) falla(error, 'guardar la actividad');
  if (!data?.length) throw new Error('No tienes permiso para cambiar la actividad.');
}

export async function borrarActividades(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const { error } = await supabase.from('annual_program_activities').delete().in('id', ids);
  if (error) falla(error, 'quitar las actividades');
}

/** Congela el contenido (con su SHA-256) y pasa la versión a "en firma". */
export async function prepararVersion(tipo: TipoPrograma, empresa: string, programa: FilaPrograma, v: FilaVersion, actividades: FilaActividad[]): Promise<void> {
  const contenido = contenidoVersion(tipo, empresa, programa.program_year, v, actividades);
  const hash = await hashPrograma(contenido);
  const { data, error } = await supabase.from('annual_program_versions').update({ status: 'en_firma', snapshot: contenido, content_hash: hash })
    .eq('id', v.id).select('id');
  if (error) falla(error, 'preparar el programa para firma');
  if (!data?.length) throw new Error('No tienes permiso para preparar el programa.');
}

export async function volverABorrador(v: FilaVersion): Promise<void> {
  const { data, error } = await supabase.from('annual_program_versions').update({ status: 'borrador' }).eq('id', v.id).select('id');
  if (error) falla(error, 'volver a editar el programa');
  if (!data?.length) throw new Error('No tienes permiso para cambiar el programa.');
}

const rutaPrograma = (p: Pick<FilaPrograma, 'company_id' | 'id'>, carpeta: 'firmas' | 'escaneos' | 'evidencias' | 'sut', archivo: string, marca = Date.now()) =>
  `${p.company_id}/${p.id}/${carpeta}/${marca}-${archivo}`;

async function subir(ruta: string, cuerpo: Blob, tipo: string, accion: string): Promise<void> {
  const { error } = await supabase.storage.from(BUCKET_PROGRAMAS).upload(ruta, cuerpo, { contentType: tipo, upsert: false });
  if (error) {
    const sinPermiso = /row-level security|unauthorized|403/i.test(error.message ?? '');
    throw new Error(sinPermiso ? `No tienes permiso para ${accion}.` : `No se pudo ${accion}. Intenta de nuevo.`);
  }
}

/** Revisa tipo real (cabecera) y tamaño: PDF, PNG o JPEG hasta 10 MB. */
export async function revisarArchivo(archivo: File): Promise<{ tipo: Exclude<TipoEvidencia, 'image/webp'> } | { error: string }> {
  const r = await revisarEvidencia(archivo);
  if ('error' in r) return r;
  if (r.tipo === 'image/webp') return { error: 'La foto debe ser JPG o PNG (o suba un PDF).' };
  if (archivo.size > ARCHIVO_MAX_BYTES) return { error: `El archivo supera ${ARCHIVO_MAX_MB} MB.` };
  return { tipo: r.tipo };
}

async function subirArchivo(p: FilaPrograma, carpeta: 'escaneos' | 'evidencias' | 'sut', archivo: File) {
  const rev = await revisarArchivo(archivo);
  if ('error' in rev) throw new Error(rev.error);
  const nombre = nombreSeguro(archivo.name, rev.tipo);
  const ruta = rutaPrograma(p, carpeta, nombre);
  await subir(ruta, archivo.type === rev.tipo ? archivo : new File([archivo], nombre, { type: rev.tipo }), rev.tipo, 'subir el archivo');
  return { path: ruta, name: nombre, mime: rev.tipo as string, size: archivo.size };
}

/** Sube el trazo y registra la firma (la base pone la fecha y hora del servidor). */
export async function firmarVersion(p: FilaPrograma, v: FilaVersion, firmante: FirmantePrograma, png: Blob): Promise<void> {
  if (v.status !== 'en_firma' || !v.content_hash) throw new Error('Prepare el programa para firma antes de firmar.');
  const hash = await hashFirmaPrograma(v.content_hash, firmante);
  const ruta = rutaPrograma(p, 'firmas', `v${v.version}_${firmante.rol}.png`);
  await subir(ruta, png, 'image/png', 'subir la firma');
  const { error } = await supabase.from('annual_program_signatures').insert({
    company_id: v.company_id, version_id: v.id, role: firmante.rol, signer_name: firmante.nombre.trim(),
    signer_cedula: firmante.cedula.trim(), image_path: ruta, content_hash: hash,
  });
  if (error) falla(error, 'registrar la firma');
}

export async function subirEscaneo(p: FilaPrograma, v: FilaVersion, archivo: File): Promise<void> {
  if (v.status !== 'en_firma') throw new Error('Prepare el programa para firma antes de subir el escaneo.');
  const a = await subirArchivo(p, 'escaneos', archivo);
  const { data, error } = await supabase.from('annual_program_versions')
    .update({ sign_method: 'papel', scan_path: a.path, scan_name: a.name, scan_mime: a.mime, scan_size: a.size }).eq('id', v.id).select('id');
  if (error) falla(error, 'guardar el escaneo');
  if (!data?.length) throw new Error('No tienes permiso para cambiar el programa.');
}

export async function aprobarVersion(v: FilaVersion, metodo: MetodoFirma): Promise<void> {
  const { data, error } = await supabase.from('annual_program_versions').update({ status: 'aprobada', sign_method: metodo }).eq('id', v.id).select('id');
  if (error) falla(error, 'aprobar el programa');
  if (!data?.length) throw new Error('No tienes permiso para aprobar el programa.');
}

/** Copia la versión aprobada a un borrador nuevo (las actividades conservan su clave y su ejecución). */
export async function crearVersionNueva(p: FilaPrograma, aprobada: FilaVersion, actividades: FilaActividad[], numero: number, motivo: string): Promise<void> {
  const copia = copiaParaVersionNueva(aprobada, actividades, numero, motivo);
  const { data, error } = await supabase.from('annual_program_versions')
    .insert({ ...copia.version, company_id: p.company_id, program_id: p.id }).select('id');
  if (error) falla(error, 'crear la versión nueva');
  const versionId = (data as { id: string }[])[0].id;
  if (copia.actividades.length) {
    const { error: e2 } = await supabase.from('annual_program_activities').insert(copia.actividades.map(a => ({
      ...a, company_id: p.company_id, program_id: p.id, version_id: versionId,
    })));
    if (e2) falla(e2, 'copiar las actividades');
  }
}

export async function borrarBorrador(v: FilaVersion): Promise<void> {
  const { data, error } = await supabase.from('annual_program_versions').delete().eq('id', v.id).select('id');
  if (error) falla(error, 'borrar el borrador');
  if (!data?.length) throw new Error('No tienes permiso para borrar el borrador.');
}

// ── Ejecución ──
export interface EjecucionNueva {
  activity_key: string; executed_date: string; notas: string | null; archivo?: File | null;
  training_id?: string | null; medical_exam_id?: string | null; linked_label?: string | null;
}

export async function registrarEjecucion(p: FilaPrograma, e: EjecucionNueva, existente: FilaEjecucion | null): Promise<void> {
  const archivo = e.archivo ? await subirArchivo(p, 'evidencias', e.archivo) : null;
  const fila = {
    executed_date: e.executed_date, notes: e.notas?.trim() || null,
    ...(archivo ? { evidence_path: archivo.path, evidence_name: archivo.name, evidence_mime: archivo.mime, evidence_size: archivo.size } : {}),
    ...(e.linked_label !== undefined ? { training_id: e.training_id ?? null, medical_exam_id: e.medical_exam_id ?? null, linked_label: e.linked_label } : {}),
  };
  if (existente) {
    const { data, error } = await supabase.from('annual_program_executions').update(fila).eq('id', existente.id).select('id');
    if (error) falla(error, 'guardar la ejecución');
    if (!data?.length) throw new Error('No tienes permiso para registrar la ejecución.');
    return;
  }
  const { error } = await supabase.from('annual_program_executions').insert({ ...fila, company_id: p.company_id, program_id: p.id, activity_key: e.activity_key });
  if (error) falla(error, 'registrar la ejecución');
}

export async function borrarEjecucion(e: FilaEjecucion): Promise<void> {
  const { data, error } = await supabase.from('annual_program_executions').delete().eq('id', e.id).select('id');
  if (error) falla(error, 'quitar la ejecución');
  if (!data?.length) throw new Error('No tienes permiso para quitar la ejecución.');
}

/** Guarda como ejecución los respaldos que vienen de una capacitación realizada o de un examen hecho, para que se
 *  conserven aunque después se borre el origen. Sin permiso (viewer) no hace nada. */
export async function guardarRespaldosVinculados(p: FilaPrograma, lista: { activity_key: string; executed_date: string; training_id: string | null;
  medical_exam_id: string | null; linked_label: string }[]): Promise<boolean> {
  if (!lista.length) return false;
  const { error } = await supabase.from('annual_program_executions')
    .insert(lista.map(x => ({ ...x, company_id: p.company_id, program_id: p.id })));
  return !error;
}

/** TH 8: crea la capacitación planificada de una actividad (en Capacitaciones) y la vincula. */
export async function crearCapacitacionDeActividad(companyId: string, a: FilaActividad): Promise<void> {
  const { data, error } = await supabase.from('sso_trainings').insert({
    company_id: companyId, title: a.title.trim(), topic: a.title.trim(), trainer_name: a.responsible?.trim() || null, scheduled_date: a.planned_date,
    duration_hours: a.duration_hours, status: 'planificada',
  }).select('id');
  if (error) falla(error, 'crear la capacitación');
  await editarActividad(a.id, { training_id: (data as { id: string }[])[0].id });
}

// ── Registro en el SUT ──
export async function guardarSut(p: FilaPrograma, existente: FilaSut | null, codigo: string, fecha: string, archivo: File | null): Promise<void> {
  if (!existente && !archivo) throw new Error('Suba la constancia del registro en el SUT.');
  const a = archivo ? await subirArchivo(p, 'sut', archivo) : null;
  const fila = { registration_code: codigo.trim(), registration_date: fecha,
    ...(a ? { constancia_path: a.path, constancia_name: a.name, constancia_mime: a.mime, constancia_size: a.size } : {}) };
  if (existente) {
    const { data, error } = await supabase.from('annual_program_sut').update(fila).eq('id', existente.id).select('id');
    if (error) falla(error, 'guardar el registro del SUT');
    if (!data?.length) throw new Error('No tienes permiso para cambiar el registro del SUT.');
    return;
  }
  const { error } = await supabase.from('annual_program_sut').insert({ ...fila, company_id: p.company_id, program_id: p.id });
  if (error) falla(error, 'guardar el registro del SUT');
}

// ── Archivos ──
export async function urlArchivo(ruta: string): Promise<string> {
  const { data, error } = await supabase.storage.from(BUCKET_PROGRAMAS).createSignedUrl(ruta, SEGUNDOS_URL_PROGRAMAS);
  if (error || !data?.signedUrl) throw new Error('No se pudo abrir el archivo. Intenta de nuevo.');
  return data.signedUrl;
}

/** Imagen de una firma como data URL para el PDF (null si no se pudo leer). */
export async function dataUrlFirma(ruta: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET_PROGRAMAS).download(ruta);
  if (error || !data) return null;
  return new Promise(resolve => {
    const lector = new FileReader();
    lector.onload = () => resolve(lector.result as string);
    lector.onerror = () => resolve(null);
    lector.readAsDataURL(data);
  });
}

export const actividadesDe = (todas: FilaActividad[], versionId: string | null | undefined) =>
  versionId ? ordenarActividades(todas.filter(a => a.version_id === versionId)) : [];
